import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Bar, BarChart } from 'recharts';
import DateRangePicker from './DateRangePicker';
import Card from './Card';
import InfoCard from './InfoCard';
import Skeleton from './Skeleton';
import { HeaderCell, BodyCell } from './TableCell';
import { IconChevronRight } from '../icons';
import { useTheme } from '../context/themeContext';
import { chartTheme } from '../utils/chartTheme';

// Availability history block shared by ServerDetail and AppDetail: date range
// picker, stats strip, drill-down bar chart (day -> hour -> minute) and the
// aggregated table. The two pages differ only in labels, the count keys their
// API returns and how rows are fetched, so all three are props.

// Цвета бара зависят от темы, поэтому палитра приходит параметром (`ct`),
// а не живёт модульной константой.
const barFill = (payload, ct) => {
  if (!payload.totalChecks) return ct.barEmpty;
  const uptime = parseFloat(payload.uptime);
  if (uptime >= 95) return ct.barGreen;
  if (uptime >= 80) return ct.barAmber;
  return ct.barRed;
};

const uptimeTextClass = (uptime) =>
  uptime >= 95 ? 'text-success' : uptime >= 80 ? 'text-warning' : 'text-danger';

const uptimePillClass = (uptime) =>
  uptime >= 95
    ? 'bg-success-bg text-success-ink'
    : uptime >= 80
      ? 'bg-warning-bg text-warning-ink'
      : 'bg-danger-bg text-danger';

// `fullDate` is either an ISO-ish timestamp (hour/minute granularity) or a
// plain date whose pre-formatted label lives in `date`.
const displayPeriod = (row) => {
  if (typeof row.fullDate === 'string' && row.fullDate.includes('T')) {
    const iso = row.fullDate.endsWith('Z') ? row.fullDate : row.fullDate + 'Z';
    return new Date(iso).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'UTC',
    });
  }
  return row.date;
};

const defaultDates = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 2); // Last 3 days
  return {
    start: start.toISOString().split('T')[0],
    end: end.toISOString().split('T')[0],
  };
};

const granularityFor = (startDate, endDate) => {
  const hoursDiff = (endDate - startDate) / (1000 * 60 * 60);
  if (hoursDiff <= 1) return 'minute';
  if (hoursDiff <= 72) return 'hour';
  return 'day';
};

const COL = { period: 200, uptime: 120, total: 120, ok: 110, bad: 110, response: 130 };

// Высоты столбиков скелетона графика, % от области построения. Фиксированные,
// а не случайные: иначе на каждом рендере плашки прыгали бы.
const SKELETON_BARS = [92, 96, 88, 100, 94, 97, 85, 99, 93, 100, 90, 95, 98, 87, 100, 94, 96, 91, 99, 95];

const UptimeHistory = ({
  title = 'Uptime History',
  metricLabel = 'Uptime',
  okKey,
  badKey,
  okLabel,
  badLabel,
  fetchRows,
  reloadToken = 0,
  sideCard,
  onError,
}) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(defaultDates);
  const [drillDownStack, setDrillDownStack] = useState([]);
  const [viewRange, setViewRange] = useState(null);
  const [hoveredBar, setHoveredBar] = useState(null);

  const { resolved } = useTheme();
  const ct = chartTheme(resolved);

  const chartScrollRef = useRef(null);
  const longPressTimer = useRef(null);
  const longPressTriggered = useRef(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const isPrecise = !!viewRange;
      const startDate = isPrecise
        ? new Date(viewRange.start)
        : new Date(dateRange.start + 'T00:00:00.000Z');
      const endDate = isPrecise
        ? new Date(viewRange.end)
        : new Date(dateRange.end + 'T23:59:59.999Z');
      const granularity = granularityFor(startDate, endDate);

      const data = await fetchRows({
        startDate: isPrecise ? startDate.toISOString() : startDate.toISOString().split('T')[0],
        endDate: isPrecise ? endDate.toISOString() : endDate.toISOString().split('T')[0],
        granularity,
      });
      setRows(data || []);
      onError?.(null);
    } catch (err) {
      console.error('Error fetching history:', err);
      onError?.('Failed to load history');
      setRows([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, viewRange, fetchRows, reloadToken]);

  useEffect(() => { load(); }, [load]);

  // Scroll chart to the end when history data loads
  useEffect(() => {
    if (chartScrollRef.current && rows.length > 0) {
      chartScrollRef.current.scrollLeft = chartScrollRef.current.scrollWidth;
    }
  }, [rows]);

  const handleDateChange = (start, end) => {
    setDateRange({ start, end });
    setViewRange(null);
    setDrillDownStack([]);
  };

  const handleBarClick = (data) => {
    if (!data) return;
    if (longPressTriggered.current) {
      longPressTriggered.current = false;
      return;
    }

    const currentRange = viewRange || dateRange;
    const startDate = viewRange
      ? new Date(currentRange.start)
      : new Date(currentRange.start + 'T00:00:00.000Z');
    const endDate = viewRange
      ? new Date(currentRange.end)
      : new Date(currentRange.end + 'T23:59:59.999Z');
    const hoursDiff = (endDate - startDate) / (1000 * 60 * 60);

    // Already at minute level — nothing finer to drill into.
    if (hoursDiff <= 1) return;

    setDrillDownStack([...drillDownStack, currentRange]);

    if (hoursDiff > 72) {
      // Days -> hours for the clicked day (00:00–23:59 UTC)
      const [year, month, day] = data.fullDate.split('-').map(Number);
      setViewRange({
        start: Date.UTC(year, month - 1, day, 0, 0, 0, 0),
        end: Date.UTC(year, month - 1, day, 23, 59, 59, 999),
      });
    } else {
      // Hours -> minutes for the clicked hour
      const isoWithZ = data.fullDate.endsWith('Z') ? data.fullDate : data.fullDate + 'Z';
      const clickedHour = new Date(isoWithZ);
      const endHour = new Date(clickedHour);
      endHour.setUTCMinutes(59, 59, 999);
      setViewRange({ start: clickedHour.getTime(), end: endHour.getTime() });
    }
  };

  const handleTouchStart = (data) => {
    longPressTriggered.current = false;
    longPressTimer.current = setTimeout(() => {
      longPressTriggered.current = true;
      handleBarClick(data);
    }, 500);
  };

  const cancelLongPress = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const handleDrillUp = () => {
    if (drillDownStack.length === 0) return;
    const previousRange = drillDownStack[drillDownStack.length - 1];
    setViewRange(typeof previousRange.start === 'number' ? previousRange : null);
    setDrillDownStack(drillDownStack.slice(0, -1));
  };

  // X axis label density
  const tickInterval = useMemo(() => {
    if (rows.length === 0) return 0;
    const first = rows[0];
    if (first.date.includes(':') && first.date.length <= 5) {
      return Math.floor(rows.length / 12); // ~12 labels for minute view
    }
    if (rows.some((item) => item.date === '')) return 0; // hourly across days
    if (rows.length > 90) return Math.floor(rows.length / 12);
    if (rows.length > 60) return Math.floor(rows.length / 10);
    if (rows.length > 30) return Math.floor(rows.length / 8);
    if (rows.length > 14) return 1;
    return 0;
  }, [rows]);

  const stats = useMemo(() => {
    const totalChecks = rows.reduce((sum, h) => sum + (h.totalChecks || 0), 0);
    const ok = rows.reduce((sum, h) => sum + (h[okKey] || 0), 0);
    const bad = rows.reduce((sum, h) => sum + (h[badKey] || 0), 0);
    const responseSum = rows.reduce(
      (sum, h) => sum + (h.avgResponseTime || 0) * (h.totalChecks || 0), 0);
    return {
      totalChecks,
      ok,
      bad,
      uptime: totalChecks > 0 ? ((ok / totalChecks) * 100).toFixed(2) : '0.00',
      avgResponseTime: totalChecks > 0 ? Math.round(responseSum / totalChecks) : 0,
    };
  }, [rows, okKey, badKey]);

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 lg:items-stretch">
        {sideCard && <div className="lg:col-span-3 flex">{sideCard}</div>}
        <div className={`${sideCard ? 'lg:col-span-9' : 'lg:col-span-12'} flex`}>
          <DateRangePicker
            startDate={dateRange.start}
            endDate={dateRange.end}
            onDateChange={handleDateChange}
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <InfoCard label={metricLabel} value={`${stats.uptime}%`} accent={uptimeTextClass(parseFloat(stats.uptime))} loading={loading} />
        <InfoCard label="Total checks" value={stats.totalChecks} loading={loading} />
        <InfoCard label={okLabel} value={stats.ok} accent="text-success" loading={loading} />
        <InfoCard label={badLabel} value={stats.bad} accent="text-danger" loading={loading} />
        <InfoCard label="Avg response" value={`${stats.avgResponseTime} ms`} loading={loading} />
      </div>

      {/* Chart */}
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 sm:px-6 pt-5">
          <div className="flex items-center gap-3">
            {drillDownStack.length > 0 && (
              <button
                onClick={handleDrillUp}
                className="inline-flex items-center gap-1 text-xs text-ink-muted hover:text-ink transition-colors"
              >
                <IconChevronRight size={14} className="rotate-180" />
                Back
              </button>
            )}
            <h2 className="text-[15px] font-medium text-ink">{title}</h2>
          </div>
          {viewRange && (
            <p className="text-xs text-ink-muted">
              {new Date(viewRange.start).toLocaleString('en-US', {
                month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                hour12: false, timeZone: 'UTC',
              })}
              {' – '}
              {new Date(viewRange.end).toLocaleString('en-US', {
                hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC',
              })} UTC
            </p>
          )}
        </div>

        {loading ? (
          // Та же коробка, что у графика (pt-4 + 300px + pb-6): столбики
          // над полосой под ось X высотой 40px, как у XAxis.
          <div className="px-4 sm:px-6 pb-6 pt-4" aria-hidden="true">
            <div className="h-[300px] flex flex-col">
              <div className="flex-1 flex items-end gap-1.5 pl-10">
                {SKELETON_BARS.map((height, index) => (
                  <Skeleton key={index} width={null} height={`${height}%`} className="flex-1" />
                ))}
              </div>
              <div className="h-10 pl-10 pt-3">
                <Skeleton width="100%" height={10} />
              </div>
            </div>
          </div>
        ) : rows.length === 0 ? (
          <div className="py-16 text-center text-sm text-ink-muted">
            No check data available for the selected date range
          </div>
        ) : (
          <div ref={chartScrollRef} className="overflow-x-auto px-4 sm:px-6 pb-6 pt-4">
            <div style={{ minWidth: Math.max(600, rows.length * 20) + 'px' }}>
              <ResponsiveContainer width="100%" height={300} style={{ outline: 'none' }}>
                <BarChart data={rows} style={{ outline: 'none' }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={ct.gridLine} />
                  <XAxis
                    dataKey="fullDate"
                    tick={{ fontSize: 10, fill: ct.textSecondary }}
                    axisLine={false}
                    tickLine={false}
                    height={40}
                    interval={tickInterval}
                    tickFormatter={(fullDate) => rows.find((d) => d.fullDate === fullDate)?.date || ''}
                  />
                  <YAxis
                    domain={[0, 100]}
                    tick={{ fontSize: 11, fill: ct.textSecondary }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    cursor={{ fill: ct.cursorFill }}
                    content={({ active, payload }) => {
                      if (!active || !payload || !payload.length) return null;
                      const data = payload[0].payload;
                      const uptimeValue = parseFloat(data.uptime);
                      const hasData = data.totalChecks > 0;
                      return (
                        <div className="bg-card border border-line rounded-xl px-3 py-2.5 shadow-dropdown">
                          <p className="text-xs font-medium text-ink">{displayPeriod(data)}</p>
                          {hasData ? (
                            <>
                              <p className={`text-xs font-medium mt-1 ${uptimeTextClass(uptimeValue)}`}>
                                {metricLabel}: {data.uptime}%
                              </p>
                              <p className="text-xs text-ink-muted">{okLabel}: {data[okKey]}</p>
                              <p className="text-xs text-ink-muted">{badLabel}: {data[badKey]}</p>
                              <p className="text-xs text-ink-muted">
                                Avg response: {Math.round(data.avgResponseTime)} ms
                              </p>
                            </>
                          ) : (
                            <p className="text-xs text-ink-muted italic mt-1">No data available</p>
                          )}
                        </div>
                      );
                    }}
                  />
                  <Bar
                    dataKey="barHeight"
                    radius={[4, 4, 0, 0]}
                    fill={ct.barGreen}
                    cursor="pointer"
                    // The custom `shape` below does not re-render reliably
                    // through recharts' grow animation, leaving bars stuck at
                    // a sliver of their height.
                    isAnimationActive={false}
                    onClick={handleBarClick}
                    onMouseEnter={(data) => setHoveredBar(data.fullDate)}
                    onMouseLeave={() => setHoveredBar(null)}
                    shape={(props) => {
                      const { x, y, width, height, payload } = props;
                      const isHovered = hoveredBar === payload.fullDate;
                      return (
                        <g>
                          <rect
                            x={x} y={y} width={width} height={height}
                            fill={barFill(payload, ct)} rx={4} ry={4}
                            opacity={isHovered ? 0.9 : 1}
                            onTouchStart={() => handleTouchStart(payload)}
                            onTouchEnd={cancelLongPress}
                            onTouchMove={cancelLongPress}
                            style={{
                              cursor: 'pointer',
                              outline: 'none',
                              transition: 'all 0.2s ease',
                              WebkitTouchCallout: 'none',
                              WebkitUserSelect: 'none',
                            }}
                          />
                          {isHovered && (
                            <>
                              <rect
                                x={x} y={y} width={width} height={height}
                                fill="none" stroke={ct.brand} strokeWidth={1} rx={4} ry={4}
                                style={{ outline: 'none', pointerEvents: 'none' }}
                              />
                              <rect
                                x={x} y={y} width={width} height={height}
                                fill={ct.brand} opacity={0.15} rx={4} ry={4}
                                style={{ outline: 'none', pointerEvents: 'none' }}
                              />
                            </>
                          )}
                        </g>
                      );
                    }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </Card>

      {/* Aggregated table */}
      {rows.length > 0 && (
        <Card>
          <div className="px-4 sm:px-6 py-4 border-b border-line">
            <h2 className="text-[15px] font-medium text-ink">Aggregated Data</h2>
          </div>
          <div className="overflow-x-auto">
            <div className="min-w-[790px]">
              <div className="flex items-stretch h-10 bg-surface border-b border-line">
                <HeaderCell width={COL.period} first grow align="left">Period</HeaderCell>
                <HeaderCell width={COL.uptime}>{metricLabel}</HeaderCell>
                <HeaderCell width={COL.total}>Total checks</HeaderCell>
                <HeaderCell width={COL.ok}>{okLabel}</HeaderCell>
                <HeaderCell width={COL.bad}>{badLabel}</HeaderCell>
                <HeaderCell width={COL.response}>Avg response</HeaderCell>
              </div>
              {[...rows].reverse().map((row, index) => {
                const uptimeValue = parseFloat(row.uptime);
                const hasData = row.totalChecks > 0;
                return (
                  <div
                    key={index}
                    className="flex items-stretch h-11 border-b border-line/60 last:border-b-0 hover:bg-surface transition-colors"
                  >
                    <BodyCell width={COL.period} first grow align="left">
                      <span className="whitespace-nowrap">{displayPeriod(row)}</span>
                    </BodyCell>
                    <BodyCell width={COL.uptime}>
                      {hasData ? (
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${uptimePillClass(uptimeValue)}`}>
                          {row.uptime}%
                        </span>
                      ) : (
                        <span className="text-ink-muted italic">No data</span>
                      )}
                    </BodyCell>
                    <BodyCell width={COL.total}>{row.totalChecks}</BodyCell>
                    <BodyCell width={COL.ok} className="text-success">{row[okKey]}</BodyCell>
                    <BodyCell width={COL.bad} className="text-danger">{row[badKey]}</BodyCell>
                    <BodyCell width={COL.response}>
                      {hasData ? `${Math.round(row.avgResponseTime)} ms` : '—'}
                    </BodyCell>
                  </div>
                );
              })}
            </div>
          </div>
        </Card>
      )}
    </>
  );
};

export default UptimeHistory;
